import { chain, filter, find, get, isEqual, map, orderBy, pick } from 'lodash';
import { OrderConfig, CustomSortConfig } from 'common/visualizations/vif';
import { HeaderRow, NormalRow, Row, SubTotalRow } from './type';
import { aggregateRows } from './gridDataAggregator';
import { TableColumn } from '../types';
import { isCustomSortEnabled } from 'common/visualizations/helpers/VifSelectors';
import { SoQLType } from 'common/types/soql';
export const groupRows = (
  rows: NormalRow[],
  tableColumns: TableColumn[],
  orderConfigs: OrderConfig[] = [],
  showSubTotal = false,
  columnSortConfig: CustomSortConfig | undefined,
): Row[] => {
  const groupSort = filter(tableColumns, (c) => c.isGrouped);
  const groupByFields = map(groupSort, (c) => c.field);
  if (!isCustomSortEnabled()) {
    const sortColumns = map(orderConfigs, 'columnName');
    const sortOrders = map(orderConfigs, (s) => (s.ascending ? 'asc' : 'desc'));
    const sortedRows = sortOrders.length > 0 ? orderBy(rows, sortColumns, sortOrders) : rows;

    if (groupByFields.length <= 0) return sortedRows;
    return processGrouping(sortedRows, groupByFields, tableColumns, showSubTotal, sortColumns, sortOrders);
  } else {
    const reorderedSortConfig = orderBy(orderConfigs, (config) => {
      const idx = groupByFields.indexOf(config.columnName);
      return idx === -1 ? Infinity : idx;
    });
    const sortColumns = map(reorderedSortConfig, (config) => config.columnName);
    const sortOrders = map(reorderedSortConfig, (s) => {
      if (s.sortToFollow == 'descending') return 'desc';
      return 'asc';
    });
    const iteratees = customSortIteratees(sortColumns, columnSortConfig, tableColumns, reorderedSortConfig);
    const sortedRows = orderBy(rows, iteratees, sortOrders);
    if (groupByFields.length <= 0) return sortedRows;
    return processGrouping(rows, groupByFields, tableColumns, showSubTotal, iteratees, sortOrders);
  }
};

const customSortIteratees = (
  sortColumns: string[],
  columnSortConfig: CustomSortConfig | undefined,
  tableColumns: TableColumn[],
  reorderedSortConfig: OrderConfig[] = [],
) => {
  return sortColumns.map((field) => {
    const regularSortConfig = find(reorderedSortConfig, ['columnName', field]);
    if (columnSortConfig === undefined || regularSortConfig?.sortToFollow != 'customSort') return field;
    const orderList = columnSortConfig[field];
    const fieldColumn = find(tableColumns, { field: field });
    if (orderList) {
      return (item: { [x: string]: any }) => {
        let value = item[field];
        if (isEqual(SoQLType.SoQLNumberT, fieldColumn?.type) && value !== undefined) {
          value = value.toString();
        }
        const index = orderList.indexOf(value);
        return index === -1 ? Number.MAX_SAFE_INTEGER : index;
      };
    }
    return field;
  });
};

const processGrouping = (
  childRows: Row[],
  currentGroupByFields: string[],
  tableColumns: TableColumn[],
  showSubTotal = false,
  sortColumns: (string | ((item: { [x: string]: any }) => number))[],
  sortOrders: ('asc' | 'desc')[] | [],
): Row[] => {
  const result = chain(childRows)
    .groupBy((row) => chain(row).pick(currentGroupByFields).values().value().join('-'))
    .map((groupedRows) => {
      const groupBys = pick(groupedRows[0], currentGroupByFields);
      const aggregations = aggregateRows(groupedRows, tableColumns);
      return {
        ...groupBys,
        ...aggregations,
        _headerRow: buildHeaderRow(groupBys, currentGroupByFields, aggregations),
        _rows: groupedRows,
        _subTotalRow: buildSubTotalRow(groupBys, aggregations, currentGroupByFields),
      };
    })
    .orderBy(sortColumns, sortOrders)
    .flatMap((group) => [group._headerRow, ...group._rows, ...(showSubTotal ? [group._subTotalRow] : [])])
    .value();

  if (currentGroupByFields.length <= 1) return result;

  return processGrouping(
    result,
    currentGroupByFields.slice(0, -1),
    tableColumns,
    showSubTotal,
    sortColumns,
    sortOrders,
  );
};

const buildHeaderRow = (
  groupBys: Partial<NormalRow>,
  groupByFields: string[],
  aggregations: object,
): HeaderRow => ({
  ...groupBys,
  ...aggregations,
  _rowType: 'header',
  _groupLevel: groupByFields.length - 1,
});

const buildSubTotalRow = (
  groupBys: Partial<NormalRow>,
  aggregations: Partial<NormalRow>,
  groupByFields: string[],
): SubTotalRow => ({
  ...groupBys,
  ...aggregations,
  _rowType: 'subTotal',
  _groupLevel: groupByFields.length - 1,
});
